import { useMemo, useState, useCallback } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { queryConversation } from 'services/api/conversation';
import { groupBy } from 'lodash-es';
import dayjs from 'lib/dayjs';
import { v4 as uuidv4 } from 'uuid';
import produce from 'immer';
import styled from '@emotion/styled';
import checkedSvg from '../assets/checked.svg';
import { Button, message, Image } from 'antd';
import { useLatest } from 'ahooks';
import StatusImage from 'views/Conversation/Sider/StatusImage';

type ImageMessageSelectProps = {
  visible: boolean;
  onCancel: () => void;
  maxLength: number;
  onSelected: (url: string[]) => void;
  conversationId: string;
};

function ImageMessageSelect(props: ImageMessageSelectProps) {
  const { visible, onCancel, maxLength, onSelected, conversationId } = props;
  const onSelectedRef = useLatest(onSelected);
  const { t } = useTranslate();
  const { data } = useRequest(
    () =>
      queryConversation({
        conversation_id: conversationId,
        page_size: 9999,
        message_type: 'IMAGE',
      }),
    {
      refreshDeps: [conversationId],
      ready: !!conversationId,
    },
  );

  const groupByDate = useMemo<[string, { date: string; uuid: string; url: string }[]][]>(() => {
    const result = data?.data?.data?.message || [];

    return Object.entries(
      groupBy(
        result.map((each) => {
          const { sendTime, imageContent } = each;
          return {
            date: dayjs(Number(sendTime)).format('YYYY-MM-DD'),
            uuid: uuidv4(),
            url: imageContent?.externalImgUrl || imageContent?.imgUrl || '',
          };
        }),
        'date',
      ),
    );
  }, [data]);

  const [selected, setSelected] = useState<{ uuid: string; url: string }[]>([]);
  const handleClick = useCallback(
    (uuid, url) => {
      setSelected(
        produce((draft) => {
          const index = draft.findIndex((e) => e.uuid === uuid);
          if (index === -1) {
            if (selected.length < maxLength) {
              draft.push({ uuid, url });
            } else {
              message.error(t('todo.最多选择x项', { x: maxLength }));
            }
          } else {
            draft.splice(index, 1);
          }
        }),
      );
    },
    [selected, maxLength, t],
  );

  return (
    <JustRightHeightModal
      visible={visible}
      centered
      width={772}
      title={t('todo.聊天记录中上传')}
      onCancel={onCancel}
      destroyOnClose
      footer={
        <div className="flex justify-end items-center">
          <div className="mr-[8px]">{t('aide.已选x项', { x: selected.length })}</div>
          <Button onClick={onCancel}>{t('phrase.取消')}</Button>
          <Button
            onClick={() => {
              onSelectedRef.current?.(selected.map((e) => e.url));
              onCancel();
            }}
            type="primary"
          >
            {t('phrase.确定')}
          </Button>
        </div>
      }
    >
      {groupByDate?.map(([date, imageList]) => (
        <div key={date}>
          <div>{date}</div>
          <div className="flex flex-wrap">
            {imageList?.map((image) => (
              <ImgWrap
                key={image.uuid}
                onClick={() => handleClick(image.uuid, image.url)}
                className={`h-[100px] w-[100px] border border-solid border-[#d8d8d8] mr-[16px] mb-[16px] hover:border-[#3366ff] ${
                  selected.some((e) => e.uuid === image.uuid) ? 'checked' : ''
                }`}
              >
                <StatusImage
                  src={image.url}
                  imageRender={(src, props) => <Image src={src} {...props} alt="" />}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </ImgWrap>
            ))}
          </div>
        </div>
      ))}
    </JustRightHeightModal>
  );
}

const ImgWrap = styled.div`
  overflow: hidden;
  &.checked {
    border-color: #3366ff;
    position: relative;
    &::after {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 20px;
      height: 20px;
      content: '';
      display: block;
      background: url(${`"${checkedSvg}"`}) no-repeat right bottom;
      background-size: 20px 20px;
    }
  }
`;

export default ImageMessageSelect;
